/**
 * SF-5080: waiting suggestions on the Items tab (manage), oldest first, with who sent them.
 * Each can be accepted as a new item (the item form, filled in from it), folded into an open
 * item of any list, archived ones too (this one unless another is picked), or declined. sf-api
 * acts only on a suggestion still waiting, so a second manager gets SUGGESTION_ALREADY_DECIDED
 * instead of a double decision. While the waiting suggestions cannot be refreshed, deciding is
 * turned off, in the row buttons and in any window already open: they may have been decided.
 */
import { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Form,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { formatShortDate } from '../../../utils/date';
import {
  acceptSuggestionArgs,
  declineSuggestionArgs,
  droppedCompaniesMessage,
  droppedCompanies,
  mergeSuggestionArgs,
  prioritiesErrorMessage,
  usePriorityItems,
  usePrioritiesWrite,
  type ItemInput,
  type PriorityItem,
  type PriorityList,
} from '../api/prioritiesClient';
import { ItemFormModal } from './ItemFormModal';
import { CompanyTags } from './CompanySelect';
import { LoadError } from './LoadError';

interface Props {
  listId: string;
  lists: PriorityList[];
  suggestions: PriorityItem[];
  loading: boolean;
  /** False while the waiting suggestions shown may be out of date (a refresh failed). */
  canDecide?: boolean;
}

const OUT_OF_DATE =
  'The waiting suggestions could not be refreshed, or this one is no longer waiting, so it cannot be decided right now.';

export const SuggestionsPanel: React.FC<Props> = ({
  listId,
  lists,
  suggestions,
  loading,
  canDecide = true,
}) => {
  const { message } = App.useApp();
  const write = usePrioritiesWrite();
  const [accepting, setAccepting] = useState<PriorityItem | null>(null);
  const [folding, setFolding] = useState<PriorityItem | null>(null);
  const [foldTarget, setFoldTarget] = useState<string | null>(null);
  // The list to fold into, this one by default; its open items are read for the window.
  const [foldList, setFoldList] = useState(listId);
  const targetsQuery = usePriorityItems('open', !!folding, foldList);
  const targets = targetsQuery.data?.data?.items ?? [];
  // Any list, archived ones too: folding in adds no item, and sf-api takes an open item of any list.
  const foldListOptions = lists.map((l) => ({ value: l.id, label: l.name }));

  const stillWaiting = (s: PriorityItem | null) => !!s && suggestions.some((w) => w.id === s.id);
  const canAccept = canDecide && stillWaiting(accepting);
  // Folding also needs the chosen list's open items read without error, with the target among them.
  const canFold =
    canDecide &&
    stillWaiting(folding) &&
    targetsQuery.isSuccess &&
    !!foldTarget &&
    targets.some((t) => t.id === foldTarget);

  // The suggestion's companies that sf-api left off the item (no longer active paid or demo).
  const showDropped = (result: unknown) => {
    const left = droppedCompaniesMessage(droppedCompanies(result));
    if (left) void message.info(left);
  };

  const accept = async (values: ItemInput) => {
    if (!accepting || !canAccept) return;
    try {
      const result = await write.run(acceptSuggestionArgs(accepting.id, values));
      void message.success('Suggestion accepted as a new item');
      showDropped(result);
    } catch (error) {
      throw new Error(prioritiesErrorMessage(error, 'The suggestion could not be accepted.'));
    }
  };

  const fold = async () => {
    if (!folding || !foldTarget || !canFold) return;
    try {
      const result = await write.run(mergeSuggestionArgs(folding.id, foldTarget));
      setFolding(null);
      void message.success('Suggestion folded in');
      showDropped(result);
    } catch (error) {
      void message.error(prioritiesErrorMessage(error, 'The suggestion could not be folded in.'));
    }
  };

  const decline = async (suggestion: PriorityItem) => {
    if (!canDecide) return;
    try {
      await write.run(declineSuggestionArgs(suggestion.id));
      void message.success('Suggestion declined');
    } catch (error) {
      void message.error(prioritiesErrorMessage(error, 'The suggestion could not be declined.'));
    }
  };

  const columns: ColumnsType<PriorityItem> = [
    {
      title: 'Suggestion',
      dataIndex: 'title',
      render: (title: string, s) => (
        <Space direction="vertical" size={0}>
          {title}
          {s.description && <Typography.Text type="secondary">{s.description}</Typography.Text>}
          <CompanyTags companies={s.companies} />
        </Space>
      ),
    },
    { title: 'From', dataIndex: 'createdByEmail', width: 220 },
    { title: 'Sent', dataIndex: 'createdAt', width: 120, render: formatShortDate },
    {
      title: 'Actions',
      key: 'actions',
      width: 260,
      render: (_, s) => (
        <Space>
          <Button
            size="small"
            disabled={!canDecide}
            aria-label={`Accept ${s.title}`}
            onClick={() => setAccepting(s)}
          >
            Accept
          </Button>
          <Button
            size="small"
            disabled={!canDecide}
            aria-label={`Fold ${s.title} into an item`}
            onClick={() => {
              setFoldTarget(null);
              setFoldList(listId);
              setFolding(s);
            }}
          >
            Fold into
          </Button>
          <Popconfirm
            title={`Decline "${s.title}"?`}
            description="The person who sent it will see it was declined."
            okText="Decline"
            okButtonProps={{ danger: true, disabled: !canDecide }}
            onConfirm={() => decline(s)}
          >
            <Button size="small" danger disabled={!canDecide} aria-label={`Decline ${s.title}`}>
              Decline
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card size="small" title={`Waiting suggestions (${suggestions.length})`}>
      <Table<PriorityItem>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={suggestions}
        pagination={false}
        locale={{ emptyText: 'No suggestions waiting' }}
      />
      <ItemFormModal
        open={!!accepting}
        item={null}
        listId={listId}
        lists={lists}
        prefill={accepting ?? undefined}
        heading="Accept suggestion"
        okText="Accept"
        canSubmit={canAccept}
        extra={
          accepting &&
          !canAccept && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={OUT_OF_DATE} />
          )
        }
        onSubmit={accept}
        onClose={() => setAccepting(null)}
      />
      <Modal
        open={!!folding}
        title={`Fold "${folding?.title ?? ''}" into an item`}
        okText="Fold in"
        okButtonProps={{ disabled: !canFold }}
        confirmLoading={write.isLoading}
        onOk={fold}
        onCancel={() => setFolding(null)}
      >
        {folding && !(canDecide && stillWaiting(folding)) && (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={OUT_OF_DATE} />
        )}
        <Form layout="vertical" component="div">
          {foldListOptions.length > 1 && (
            <Form.Item label="List" htmlFor="fold-list">
              <Select
                id="fold-list"
                aria-label="List to fold into"
                options={foldListOptions}
                value={foldList}
                onChange={(next: string) => {
                  setFoldList(next);
                  setFoldTarget(null);
                }}
              />
            </Form.Item>
          )}
          <Form.Item label="Item" htmlFor="fold-item">
            {targetsQuery.isError ? (
              <LoadError
                error={targetsQuery.error}
                fallback="The open items could not be loaded."
                onRetry={targetsQuery.refetch}
              />
            ) : (
              <Select
                id="fold-item"
                aria-label="Item to fold into"
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="label"
                placeholder="Pick an open item"
                loading={targetsQuery.isLoading}
                notFoundContent={targetsQuery.isLoading ? 'Loading…' : 'No open items in this list'}
                value={foldTarget}
                onChange={setFoldTarget}
                options={targets.map((i) => ({ value: i.id, label: i.title }))}
              />
            )}
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};
